<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { RouterLink, RouterView, useRoute, useRouter } from 'vue-router'
import { ArrowRightStartOnRectangleIcon, Bars3Icon, ChevronDownIcon, Cog6ToothIcon, KeyIcon, Squares2X2Icon, UserCircleIcon } from '@heroicons/vue/24/outline'
import flagUkraine from './assets/flags/ukraine.svg'
import flagRussia from './assets/flags/russia.svg'
import flagUnitedKingdom from './assets/flags/united-kingdom.svg'
import PageStationContext from './components/PageStationContext.vue'
import { pageViewKey } from './platform/pageContext.js'
import ScrollToTopButton from './components/ScrollToTopButton.vue'
import PwaStatus from './components/PwaStatus.vue'
import AppSidebar from './components/navigation/AppSidebar.vue'
import { useI18n } from './i18n.js'
import {
  changeReportLanguage,
  session,
  signOut,
} from './session.js'
import { roleLabelKey } from './access/roleLabels.js'
import { sessionRevokedEvent } from './api.js'
import { loadColorMappings, refreshColorMappingsOnFocus, setColorMappingScope } from './pts2/colorMappings.js'

const router = useRouter()
const route = useRoute()
const { t } = useI18n()
const desktopBreakpoint = 1024
const sidebarOpen = ref(typeof window === 'undefined' || window.innerWidth >= desktopBreakpoint)
const languageSaving = ref(false)
const languageError = ref('')
const languageMenu = ref(null)
const profileMenu = ref(null)
const reportLanguages = [
  { code: 'uk', labelKey: 'ukrainian', flag: flagUkraine },
  { code: 'ru', labelKey: 'russian', flag: flagRussia },
  { code: 'en', labelKey: 'english', flag: flagUnitedKingdom },
]

const isPts2 = computed(() => route.path.startsWith('/pts2/'))
const navigationArea = computed(() => route.path.startsWith('/platform') ? 'platform' : (isPts2.value ? 'pts2' : 'naftapos'))
const showSidebar = computed(() => session.user && (route.path.startsWith('/platform/') || route.path.startsWith('/naftapos/') || isPts2.value))
const showPhysicalStation = computed(() => session.user && route.meta.stationContext)
const showApplicationCatalogLink = computed(() => session.user
  && route.name !== 'application-catalog'
  && (route.meta.application || route.meta.platform))
const routeViewKey = computed(() => pageViewKey(route, session))

const paletteScope = computed(() => JSON.stringify([session.user?.id ?? session.user?.username, session.user?.accessVersion,
  session.user?.role, session.user?.effectivePermissions]))
watch(paletteScope, (key) => setColorMappingScope(key), { immediate: true, flush: 'sync' })
watch(() => [isPts2.value, paletteScope.value], () => {
  if (isPts2.value && session.user) loadColorMappings().catch(() => {})
}, { immediate: true })
function refreshPalette() {
  if (isPts2.value && session.user) refreshColorMappingsOnFocus().catch(() => {})
}
onMounted(() => window.addEventListener('focus', refreshPalette))
onUnmounted(() => window.removeEventListener('focus', refreshPalette))

function closeSidebar() {
  sidebarOpen.value = false
}

function closeSidebarAfterNavigation() {
  if (window.innerWidth < desktopBreakpoint) closeSidebar()
  profileMenu.value?.removeAttribute('open')
}

function roleLabel(role) {
  const key = roleLabelKey(role)
  if (key) return t(key)
  return typeof role === 'object' ? role.displayName : role
}

function activeReportLanguage() {
  return reportLanguages.find((language) => language.code === session.user?.reportLanguage) ?? reportLanguages[0]
}

function closeLanguageMenu() {
  languageMenu.value?.removeAttribute('open')
}

async function setReportLanguage(language) {
  if (language === session.user?.reportLanguage) {
    closeLanguageMenu()
    return
  }
  languageSaving.value = true
  languageError.value = ''
  try {
    await changeReportLanguage(language)
    if (route.name === 'report-detail') await router.push({ name: 'home' })
  } catch {
    languageError.value = t('languageChangeFailed')
  } finally {
    languageSaving.value = false
    closeLanguageMenu()
  }
}

async function logout() {
  try {
    await signOut()
  } finally {
    closeSidebar()
    router.push({ name: 'login' })
  }
}

function handleSessionRevoked() {
  const redirect = route.fullPath
  session.user = null
  session.applications = []
  session.platformContext = null
  session.pts2Context = null
  session.platformEntryPath = null
  session.platformShellLoaded = false
  session.initialized = true
  if (route.name !== 'login') router.push({ name: 'login', query: { redirect, reason: 'session-revoked' } })
}

onMounted(() => window.addEventListener(sessionRevokedEvent, handleSessionRevoked))
onUnmounted(() => window.removeEventListener(sessionRevokedEvent, handleSessionRevoked))
</script>

<template>
  <div class="application-shell" :class="{
    'login-shell': !session.user,
    'sidebar-expanded': showSidebar && sidebarOpen,
    'catalog-shell': route.name === 'application-catalog',
    'pts2-shell': isPts2,
  }">
    <header v-if="session.user" class="application-header">
      <div class="brand-strip"><RouterLink to="/">
        <template v-if="isPts2">Next Reports Web</template>
        <template v-else>NRW <span>· Next Reports Web</span></template>
      </RouterLink></div>
      <div class="topbar">
        <button v-if="showSidebar" class="menu-toggle" type="button" :aria-expanded="sidebarOpen" aria-controls="application-navigation"
          :aria-label="sidebarOpen ? t('menuClose') : t('menuOpen')" @click="sidebarOpen = !sidebarOpen">
          <Bars3Icon aria-hidden="true" />
        </button>
        <RouterLink class="brand" to="/">
          <strong>Next Reports</strong>
          <small>{{ navigationArea === 'platform' ? t('administration') : (isPts2 ? t('pts2UiServiceSubtitle') : t('reportingSystem')) }}</small>
        </RouterLink>
        <RouterLink v-if="showApplicationCatalogLink" class="application-catalog-link" to="/apps">
          <Squares2X2Icon aria-hidden="true" /><span>{{ t('services') }}</span>
        </RouterLink>
        <div class="header-spacer"></div>
        <details ref="languageMenu" class="language-menu">
          <summary class="language-menu-trigger" :aria-label="t('language')" :title="t(activeReportLanguage().labelKey)">
            <img :src="activeReportLanguage().flag" alt="" />
            <span class="language-menu-label">{{ t(activeReportLanguage().labelKey) }}</span>
          </summary>
          <div class="language-popover" :aria-label="t('language')" role="group">
            <button v-for="language in reportLanguages" :key="language.code" class="language-option" type="button"
              :class="{ active: session.user.reportLanguage === language.code }"
              :aria-label="t(language.labelKey)" :disabled="languageSaving || session.user.mustChangePassword"
              @click="setReportLanguage(language.code)">
              <img :src="language.flag" alt="" /><span>{{ t(language.labelKey) }}</span>
            </button>
          </div>
        </details>
        <span v-if="languageError" class="language-error" role="alert">{{ languageError }}</span>
        <details ref="profileMenu" class="profile-menu">
          <summary class="profile-trigger" :aria-label="t('profileMenu')">
            <span class="profile-avatar" aria-hidden="true"><UserCircleIcon /></span>
            <span class="profile-identity"><strong>{{ session.user.username }}</strong><small>{{ roleLabel(session.user.role) }}</small></span>
            <span class="profile-chevron" aria-hidden="true"><ChevronDownIcon /></span>
          </summary>
          <div class="profile-popover">
            <RouterLink to="/change-password" @click="closeSidebarAfterNavigation"><KeyIcon aria-hidden="true" />{{ t('changePassword') }}</RouterLink>
            <RouterLink v-if="session.platformEntryPath" :to="session.platformEntryPath" @click="closeSidebarAfterNavigation"><Cog6ToothIcon aria-hidden="true" />{{ t('administration') }}</RouterLink>
            <button type="button" @click="logout"><ArrowRightStartOnRectangleIcon aria-hidden="true" />{{ t('signOut') }}</button>
          </div>
        </details>
      </div>
    </header>
    <Transition name="sidebar">
      <button v-if="showSidebar && sidebarOpen" class="sidebar-backdrop" type="button" :aria-label="t('menuClose')" @click="closeSidebar"></button>
    </Transition>
    <Transition name="sidebar-panel">
      <AppSidebar v-if="showSidebar && sidebarOpen" :user="session.user" :area="navigationArea"
        @close="closeSidebar" @navigate="closeSidebarAfterNavigation" />
    </Transition>
    <main class="page" :class="{
      'workspace-page': route.name === 'home',
      'access-workspace-page': String(route.name || '').startsWith('access-'),
      'catalog-page-shell': route.name === 'application-catalog',
      'platform-workspace-page': navigationArea === 'platform',
      'pts2-page-shell': isPts2,
    }"><PageStationContext v-if="showPhysicalStation" :pts2="isPts2" /><RouterView :key="routeViewKey" /></main>
    <ScrollToTopButton v-if="session.user" :label="t('scrollToTop')" />
    <PwaStatus />
  </div>
</template>
